/**
 * WhatIfTab — third tab of LibraryManagerModal (Trinity-facing).
 *
 * Curates the Output/Input candidate sets exposed to the executive
 * What-If Control Bar dropdowns (`IsExecutiveVisible`, main API,
 * EXEC_WHATIF_v1_design.md D2/D3) and authors the example-question
 * library shown on the What-If HOME state (`ExecutivePrompt` rows
 * discriminated by `tool='executive_whatif_example'`, D9).
 *
 * Analyst-density UI: mirrors PromptsTab's row idioms (subtitle2
 * category-style labels, icon buttons, Switch) — this is the gated
 * management modal, not the Duplo executive theme.
 *
 * Health chip (F5, mirrors PromptsTab's health-chip idiom): fires
 * `libraryHealthCheck` for the instance alongside the examples list
 * load and maps per-prompt results onto each example row's Chip
 * (Healthy/Broken, tooltip carrying the error string when broken).
 * Unlike PromptsTab this runs automatically rather than behind a
 * manual "Run health check" button — the examples list here is small
 * (6-8 rows) and a stale-curation break is exactly the kind of thing a
 * consultant should see on open, not after remembering to click a
 * button. A failed health-check call degrades calmly: no chips render,
 * the rest of the tab is unaffected.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';

import PopupDialog from 'src/components/shared/PopupDialog';
import {
  PROMPT_TITLE_BOLD_HELP,
  renderPromptTitle,
  stripPromptMarkdown,
} from 'src/components/shared/PromptTitle';

import {
  createWhatIfExample,
  deleteLibraryPrompt,
  getWhatIfCuration,
  libraryHealthCheck,
  LibraryHealthResult,
  patchLibraryPrompt,
  putWhatIfCuration,
  WhatIfCurationEntity,
  WhatIfExample,
  WhatIfExampleParams,
  WHATIF_EXAMPLE_TOOL,
  listWhatIfExamples,
} from './api';

// ---------------------------------------------------------------------------
// Curation checklist (shared shape for outputs and inputs)
// ---------------------------------------------------------------------------

// Candidate/curated lists are presented alphabetically. The curation
// endpoints return entity order (id/creation), which is meaningless to a
// consultant scanning for a name; localeCompare with numeric collation
// keeps names like "Patients 2" ahead of "Patients 10".
const byName = (a: WhatIfCurationEntity, b: WhatIfCurationEntity): number =>
  a.name.localeCompare(b.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  });

interface CurationListProps {
  label: string;
  entities: WhatIfCurationEntity[];
  checked: Set<number>;
  onToggle: (id: number) => void;
}

const CurationList: React.FC<CurationListProps> = ({
  label,
  entities,
  checked,
  onToggle,
}) => {
  // Per-list filter state: each CurationList instance (Outputs, Inputs) keeps its own
  // search term and Selected-only toggle, so filtering one list never touches the other.
  const [search, setSearch] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);

  // Sort once, then filter by name substring (case-insensitive) and, optionally, to
  // currently-checked rows. Selected-only reads live `checked`, so a row unchecked while
  // the toggle is on drops out immediately — the consultant sees exactly what's selected.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...entities]
      .sort(byName)
      .filter((e) => (term ? e.name.toLowerCase().includes(term) : true))
      .filter((e) => (selectedOnly ? checked.has(e.id) : true));
  }, [entities, search, selectedOnly, checked]);

  return (
    <Box sx={{ flex: 1, minWidth: 260 }}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}
      >
        {label}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {entities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No AI-enabled candidates found for this instance.
        </Typography>
      ) : (
        <>
          <TextField
            size="small"
            fullWidth
            placeholder={`Search ${label.toLowerCase()}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            sx={{ ml: 0, mb: 0.5 }}
            control={
              <Switch
                size="small"
                checked={selectedOnly}
                onChange={(e) => setSelectedOnly(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Selected only
              </Typography>
            }
          />
          <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
            {visible.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                {selectedOnly && !search.trim()
                  ? 'None selected yet.'
                  : 'No matches.'}
              </Typography>
            ) : (
              visible.map((entity) => (
                <FormControlLabel
                  key={entity.id}
                  sx={{ display: 'flex', py: 0, ml: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked.has(entity.id)}
                      onChange={() => onToggle(entity.id)}
                    />
                  }
                  label={
                    <Typography variant="body2" noWrap>
                      {entity.name}
                    </Typography>
                  }
                />
              ))
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// WhatIfTab
// ---------------------------------------------------------------------------

const WhatIfTab: React.FC<{ instanceId: number }> = ({ instanceId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [outputs, setOutputs] = useState<WhatIfCurationEntity[]>([]);
  const [inputs, setInputs] = useState<WhatIfCurationEntity[]>([]);
  const [checkedOutputs, setCheckedOutputs] = useState<Set<number>>(new Set());
  const [checkedInputs, setCheckedInputs] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Stale-fetch guard: WhatIfTab stays mounted across instance switches
  // (same purpose as ConfigTab's `cancelled` flag above, adapted for a
  // reload callback that's also invoked manually after a save — the ref
  // always holds the *current* instanceId, so a response for a since-
  // abandoned instance is dropped instead of overwriting fresher data).
  const currentInstanceId = useRef(instanceId);
  currentInstanceId.current = instanceId;

  const reloadCuration = useCallback(() => {
    const requestedInstanceId = instanceId;
    setLoading(true);
    setError(null);
    getWhatIfCuration(instanceId)
      .then((resp) => {
        if (currentInstanceId.current !== requestedInstanceId) return;
        setOutputs(resp.outputs ?? []);
        setInputs(resp.inputs ?? []);
        setCheckedOutputs(
          new Set(
            (resp.outputs ?? [])
              .filter((o) => o.is_executive_visible)
              .map((o) => o.id)
          )
        );
        setCheckedInputs(
          new Set(
            (resp.inputs ?? [])
              .filter((i) => i.is_executive_visible)
              .map((i) => i.id)
          )
        );
      })
      .catch(() => {
        if (currentInstanceId.current !== requestedInstanceId) return;
        setError('Failed to load What-If curation candidates.');
      })
      .finally(() => {
        if (currentInstanceId.current !== requestedInstanceId) return;
        setLoading(false);
      });
  }, [instanceId]);

  useEffect(() => {
    reloadCuration();
  }, [reloadCuration]);

  const dirty = useMemo(() => {
    const savedOutputs = new Set(
      outputs.filter((o) => o.is_executive_visible).map((o) => o.id)
    );
    const savedInputs = new Set(
      inputs.filter((i) => i.is_executive_visible).map((i) => i.id)
    );
    const sameSet = (a: Set<number>, b: Set<number>) =>
      a.size === b.size && [...a].every((x) => b.has(x));
    return (
      !sameSet(checkedOutputs, savedOutputs) ||
      !sameSet(checkedInputs, savedInputs)
    );
  }, [outputs, inputs, checkedOutputs, checkedInputs]);

  const toggleOutput = (id: number) => {
    setCheckedOutputs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleInput = (id: number) => {
    setCheckedInputs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSaveCuration = async () => {
    setSaving(true);
    try {
      // Single PUT carries both lists: the endpoint sets IsExecutiveVisible
      // for exactly the listed ids and clears it for every other candidate
      // of the instance, for both entity types, in one transaction.
      await putWhatIfCuration(instanceId, {
        output_ids: [...checkedOutputs],
        input_ids: [...checkedInputs],
      });
      enqueueSnackbar('What-If curation saved.', { variant: 'success' });
      reloadCuration();
    } catch (e) {
      enqueueSnackbar(
        e instanceof Error ? e.message : 'Failed to save What-If curation.',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  // Curated (executive-visible) entities feed the examples editor — an
  // example must reference entities that are ACTUALLY curated, i.e. the
  // last-persisted IsExecutiveVisible flag, not the in-progress checkbox
  // state. Using the live checked Sets here would let a consultant author
  // an example against an entity that's still IsExecutiveVisible=0 in the
  // DB until "Save curation" is clicked.
  const curatedOutputs = useMemo(
    () => outputs.filter((o) => o.is_executive_visible).sort(byName),
    [outputs]
  );
  const curatedInputs = useMemo(
    () => inputs.filter((i) => i.is_executive_visible).sort(byName),
    [inputs]
  );

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading What-If curation…
      </Typography>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        What-If is live for executives on this brand only when at least one
        output and one input are curated below — empty curation disables the nav
        item on the Executive View.
      </Typography>

      <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
        <CurationList
          label="Inputs"
          entities={inputs}
          checked={checkedInputs}
          onToggle={toggleInput}
        />
        <CurationList
          label="Outputs"
          entities={outputs}
          checked={checkedOutputs}
          onToggle={toggleOutput}
        />
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleSaveCuration}
          disabled={!dirty || saving}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save curation'}
        </Button>
        {dirty && (
          <Typography variant="caption" color="text.secondary">
            Unsaved changes
          </Typography>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <ExamplesEditor
        instanceId={instanceId}
        curatedOutputs={curatedOutputs}
        curatedInputs={curatedInputs}
        curationDirty={dirty}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Examples editor
// ---------------------------------------------------------------------------

const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

const ExamplesEditor: React.FC<{
  instanceId: number;
  curatedOutputs: WhatIfCurationEntity[];
  curatedInputs: WhatIfCurationEntity[];
  curationDirty: boolean;
}> = ({ instanceId, curatedOutputs, curatedInputs, curationDirty }) => {
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  const [examples, setExamples] = useState<WhatIfExample[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Per-example health chip (F5): prompt_id -> health result. A failed
  // health-check call just leaves this empty — no chip renders, calm
  // degrade, the rest of the tab is unaffected.
  const [health, setHealth] = useState<Map<number, LibraryHealthResult>>(
    new Map()
  );

  // Stale-fetch guard, same pattern as WhatIfTab.reloadCuration above.
  const currentInstanceId = useRef(instanceId);
  currentInstanceId.current = instanceId;

  const reload = useCallback(() => {
    const requestedInstanceId = instanceId;
    setLoading(true);
    setError(null);
    setHealth(new Map());
    listWhatIfExamples(instanceId)
      .then((resp) => {
        if (currentInstanceId.current !== requestedInstanceId) return;
        const rows = resp.prompts ?? [];
        setExamples(rows.filter((r) => r.tool === WHATIF_EXAMPLE_TOOL));
      })
      .catch(() => {
        if (currentInstanceId.current !== requestedInstanceId) return;
        setError('Failed to load What-If examples.');
      })
      .finally(() => {
        if (currentInstanceId.current !== requestedInstanceId) return;
        setLoading(false);
      });
    // Health check runs alongside the list load rather than gated on
    // its result — `POST /v2/library/health` resolves every prompt
    // (CV + example rows) for the instance in one call regardless of
    // what listWhatIfExamples returns, and a failure here must not
    // block or blemish the examples list itself (calm degrade, F5).
    libraryHealthCheck(instanceId)
      .then((resp) => {
        if (currentInstanceId.current !== requestedInstanceId) return;
        setHealth(new Map(resp.results.map((r) => [r.prompt_id, r])));
      })
      .catch(() => {
        // No chips render; nothing else about the tab is affected.
      });
  }, [instanceId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const outputById = useMemo(
    () => new Map(curatedOutputs.map((o) => [o.id, o.name])),
    [curatedOutputs]
  );
  const inputById = useMemo(
    () => new Map(curatedInputs.map((i) => [i.id, i.name])),
    [curatedInputs]
  );

  const parseParams = (example: WhatIfExample): WhatIfExampleParams | null => {
    try {
      const parsed = JSON.parse(example.params);
      if (
        typeof parsed?.output_id === 'number' &&
        typeof parsed?.input_id === 'number' &&
        typeof parsed?.change_text === 'string'
      ) {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  };

  const move = async (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= examples.length) return;
    const next = [...examples];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    try {
      await Promise.all(
        next
          .map((p, i) => ({ p, i }))
          .filter(({ p, i }) => p.sort_order !== i)
          .map(({ p, i }) => patchLibraryPrompt(p.id, { sort_order: i }))
      );
      reload();
    } catch {
      enqueueSnackbar('Failed to reorder examples.', { variant: 'error' });
    }
  };

  const toggleEnabled = async (example: WhatIfExample) => {
    try {
      await patchLibraryPrompt(example.id, {
        enabled: example.enabled === 1 ? 0 : 1,
      });
      reload();
    } catch {
      enqueueSnackbar('Failed to update the example.', { variant: 'error' });
    }
  };

  const remove = (example: WhatIfExample) => {
    confirm({
      description: `Delete "${stripPromptMarkdown(example.title)}" from the What-If examples? Executives will no longer see it.`,
    })
      .then(async () => {
        try {
          await deleteLibraryPrompt(example.id);
          reload();
        } catch {
          enqueueSnackbar('Failed to delete the example.', {
            variant: 'error',
          });
        }
      })
      .catch(() => undefined);
  };

  const curationEmpty =
    curatedOutputs.length === 0 || curatedInputs.length === 0;
  const addDisabled = curationEmpty || curationDirty;
  const addDisabledReason = curationDirty
    ? 'Save curation changes first'
    : curationEmpty
      ? 'Curate at least one output and one input first'
      : '';

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}
        >
          Examples
        </Typography>
        <Tooltip title={addDisabledReason}>
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setAddOpen(true)}
              disabled={addDisabled}
              sx={{ textTransform: 'none' }}
            >
              Add example
            </Button>
          </span>
        </Tooltip>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        6–8 examples works best.
      </Typography>
      {curationDirty ? (
        <Alert severity="info" sx={{ mb: 1, py: 0 }}>
          Curation has unsaved changes — save curation above before authoring
          examples.
        </Alert>
      ) : (
        curationEmpty && (
          <Alert severity="info" sx={{ mb: 1, py: 0 }}>
            Curate at least one output and one input above before authoring
            examples — examples may only reference executive-visible entities.
          </Alert>
        )
      )}
      <Divider sx={{ mb: 1 }} />

      {loading && (
        <Typography variant="body2" color="text.secondary">
          Loading examples…
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && examples.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No What-If examples authored for this instance yet.
        </Typography>
      )}

      {examples.map((example, index) => {
        const params = parseParams(example);
        const outputName = params
          ? outputById.get(params.output_id)
          : undefined;
        const inputName = params ? inputById.get(params.input_id) : undefined;
        const rowHealth = health.get(example.id);
        return (
          <Stack
            key={example.id}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ py: 0.75 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                {renderPromptTitle(example.title)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                display="block"
              >
                {outputName ?? '(output not curated)'} ·{' '}
                {inputName ?? '(input not curated)'}
                {params ? ` · ${truncate(params.change_text, 80)}` : ''}
              </Typography>
            </Box>
            {rowHealth && (
              <Tooltip
                title={
                  rowHealth.healthy
                    ? 'Bindings resolve against the current model instance.'
                    : rowHealth.errors.join(' · ')
                }
              >
                <Chip
                  size="small"
                  label={rowHealth.healthy ? 'Healthy' : 'Broken'}
                  color={rowHealth.healthy ? 'success' : 'error'}
                  variant="outlined"
                />
              </Tooltip>
            )}
            <Tooltip title="Move up">
              <span>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down">
              <span>
                <IconButton
                  size="small"
                  disabled={index === examples.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={example.enabled === 1 ? 'Visible to executives' : 'Hidden'}
            >
              <Switch
                size="small"
                checked={example.enabled === 1}
                onChange={() => toggleEnabled(example)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => remove(example)}>
                <DeleteOutlineIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      })}

      <AddExampleDialog
        open={addOpen}
        instanceId={instanceId}
        curatedOutputs={curatedOutputs}
        curatedInputs={curatedInputs}
        onClose={() => setAddOpen(false)}
        onSaved={() => {
          setAddOpen(false);
          reload();
        }}
      />
    </Box>
  );
};

const AddExampleDialog: React.FC<{
  open: boolean;
  instanceId: number;
  curatedOutputs: WhatIfCurationEntity[];
  curatedInputs: WhatIfCurationEntity[];
  onClose: () => void;
  onSaved: () => void;
}> = ({
  open,
  instanceId,
  curatedOutputs,
  curatedInputs,
  onClose,
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [outputId, setOutputId] = useState<number | ''>('');
  const [inputId, setInputId] = useState<number | ''>('');
  const [changeText, setChangeText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle('');
      setOutputId('');
      setInputId('');
      setChangeText('');
      setSaving(false);
    }
  }, [open]);

  const handleSave = async () => {
    if (outputId === '' || inputId === '') return;
    setSaving(true);
    try {
      await createWhatIfExample({
        model_instance_id: instanceId,
        title: title.trim(),
        output_id: outputId,
        input_id: inputId,
        change_text: changeText.trim(),
      });
      onSaved();
    } catch (e) {
      setSaving(false);
      enqueueSnackbar(
        e instanceof Error ? e.message : 'Failed to save the example.',
        { variant: 'error' }
      );
    }
  };

  return (
    <PopupDialog
      open={open}
      close={onClose}
      title="Add What-If Example"
      maxWidth="sm"
      primaryButtons={[
        {
          id: 'save',
          label: 'Save',
          onClick: handleSave,
          disabled:
            !title.trim() ||
            !changeText.trim() ||
            outputId === '' ||
            inputId === '' ||
            saving,
          loading: saving,
          variant: 'contained',
        },
      ]}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label="Executive-facing title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          inputProps={{ maxLength: 255 }}
          helperText={PROMPT_TITLE_BOLD_HELP}
        />
        <FormControl fullWidth required size="small">
          <InputLabel id="whatif-example-output-label">Output</InputLabel>
          <Select
            labelId="whatif-example-output-label"
            label="Output"
            value={outputId}
            onChange={(e) => setOutputId(e.target.value as number)}
          >
            {curatedOutputs.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth required size="small">
          <InputLabel id="whatif-example-input-label">Input</InputLabel>
          <Select
            labelId="whatif-example-input-label"
            label="Input"
            value={inputId}
            onChange={(e) => setInputId(e.target.value as number)}
          >
            {curatedInputs.map((i) => (
              <MenuItem key={i.id} value={i.id}>
                {i.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Change (in this way)"
          placeholder="e.g. increases 10% in the US and Germany from 2027"
          value={changeText}
          onChange={(e) => setChangeText(e.target.value)}
          fullWidth
          required
          multiline
          minRows={2}
          inputProps={{ maxLength: 1000 }}
        />
      </Stack>
    </PopupDialog>
  );
};

export default WhatIfTab;
